import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { ArrowLeft, ChevronRight, Target, TrendingUp, BarChart3, CheckCircle2, Clock, AlertCircle, Lock, Download, Loader } from 'lucide-react'
import pptxgen from 'pptxgenjs'
import DRLProgressBars from '../components/etapa0/DRLProgressBars'

export default function ConsultancyDashboardPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isNVC, isAdmin } = useAuth()

  const [consultancy, setConsultancy] = useState(null)
  const [bus, setBus] = useState([])
  const [buData, setBuData] = useState({}) // { buId: { phases, stratObj, pillars, metrics, portfolio, priceStrat } }
  const [loading, setLoading] = useState(true)
  const [exportingPPT, setExportingPPT] = useState(false)

  useEffect(() => { fetchAll() }, [id])

  async function fetchAll() {
    setLoading(true)
    const [{ data: cons }, { data: busData }] = await Promise.all([
      supabase.from('consultancies').select('id, name, client_company, industry, status').eq('id', id).single(),
      supabase.from('business_units').select('id, name, description, status').eq('consultancy_id', id).order('created_at'),
    ])
    setConsultancy(cons)
    setBus(busData ?? [])

    if (busData?.length) {
      const dataMap = {}
      await Promise.all(busData.map(async (bu) => {
        // Fetch phases
        const { data: phases } = await supabase
          .from('phases')
          .select('id, name, status, progress, is_locked, phase_order')
          .eq('business_unit_id', bu.id)
          .order('phase_order')

        // Find strategy/definicion phase
        const stratPhase = (phases ?? []).find(p => {
          const n = (p.name ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          return n.includes('estrategia') || n.includes('definicion')
        })

        let stratObj = null, pillars = [], metrics = [], portfolio = null, priceStrat = null

        if (stratPhase) {
          const { data: sections } = await supabase
            .from('sections')
            .select('id, name, data_pct, tabs_with_data')
            .eq('phase_id', stratPhase.id)

          const objSec = sections?.find(s => {
            const n = (s.name ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            return n.includes('objetivo')
          })
          const portSec = sections?.find(s => {
            const n = (s.name ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            return n.includes('portafolio') && !n.includes('competidor')
          })
          const priceSec = sections?.find(s => {
            const n = (s.name ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            return n.includes('precio')
          })

          if (objSec) {
            const [{ data: so }, { data: pils }] = await Promise.all([
              supabase.from('strategic_objectives').select('*').eq('section_id', objSec.id).maybeSingle(),
              supabase.from('strategic_pillars').select('*').eq('section_id', objSec.id).order('pillar_order'),
            ])
            stratObj = so
            pillars = pils ?? []
            if (so) {
              const { data: mx } = await supabase.from('strategic_objective_metrics')
                .select('*').eq('strategic_objective_id', so.id).order('year')
              metrics = mx ?? []
            }
          }
          if (portSec) {
            const { data: p } = await supabase.from('bu_strategy_portfolio').select('*').eq('section_id', portSec.id).maybeSingle()
            portfolio = p
          }
          if (priceSec) {
            const { data: ps } = await supabase.from('bu_price_strategy').select('*').eq('section_id', priceSec.id).maybeSingle()
            priceStrat = ps
          }
        }

        dataMap[bu.id] = { phases: phases ?? [], stratObj, pillars, metrics, portfolio, priceStrat }
      }))
      setBuData(dataMap)
    }
    setLoading(false)
  }

  function getPhaseHealth(phases) {
    if (!phases.length) return 'empty'
    const avg = phases.reduce((s, p) => s + (p.progress ?? 0), 0) / phases.length
    if (avg >= 80) return 'green'
    if (avg >= 40) return 'yellow'
    return 'red'
  }

  const HEALTH_COLORS = {
    green:  { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', label: 'Avanzado' },
    yellow: { bg: 'bg-amber-100',   text: 'text-amber-700',   border: 'border-amber-200',   dot: 'bg-amber-500',   label: 'En progreso' },
    red:    { bg: 'bg-red-100',     text: 'text-red-700',     border: 'border-red-200',     dot: 'bg-red-400',     label: 'Inicial' },
    empty:  { bg: 'bg-gray-100',    text: 'text-gray-500',    border: 'border-gray-200',    dot: 'bg-gray-400',    label: 'Sin etapas' },
  }

  const overallProgress = bus.length
    ? Math.round(bus.reduce((s, bu) => {
        const d = buData[bu.id]
        if (!d?.phases?.length) return s
        return s + d.phases.reduce((ps, p) => ps + (p.progress ?? 0), 0) / d.phases.length
      }, 0) / bus.length)
    : 0

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand-500 border-t-transparent" />
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 text-sm text-gray-400 flex-wrap">
        <button onClick={() => navigate('/')} className="hover:text-brand-600 transition-colors">Dashboard</button>
        <ChevronRight size={14} className="flex-shrink-0" />
        <button onClick={() => navigate(`/consultancy/${id}`)} className="hover:text-brand-600 transition-colors truncate max-w-[160px]">
          {consultancy?.client_company ?? 'Consultoría'}
        </button>
        <ChevronRight size={14} className="flex-shrink-0" />
        <span className="text-gray-700 font-medium">Cuadro de Mando</span>
      </div>

      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-brand-700 to-brand-900 p-6 text-white shadow-lg">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-brand-200 text-sm font-medium mb-1">{consultancy?.client_company}</p>
            <h1 className="text-2xl font-bold">Cuadro de Mando Estratégico</h1>
            <p className="text-brand-300 text-sm mt-1">{bus.length} unidades de negocio · {consultancy?.industry ?? ''}</p>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-4xl font-bold">{overallProgress}%</div>
            <div className="text-brand-300 text-xs mt-0.5">Progreso global</div>
          </div>
        </div>
        {/* Overall progress bar */}
        <div className="mt-5">
          <div className="h-2 bg-brand-800 rounded-full overflow-hidden">
            <div className="h-full bg-brand-300 rounded-full transition-all duration-700" style={{ width: `${overallProgress}%` }} />
          </div>
        </div>
      </div>

      {/* DRL — Información necesaria del proyecto */}
      <DRLProgressBars consultancyId={id} />

      {/* BU Cards */}
      <div className="space-y-5">
        {bus.map(bu => {
          const d = buData[bu.id] ?? {}
          const health = getPhaseHealth(d.phases ?? [])
          const hc = HEALTH_COLORS[health]
          const avgProgress = d.phases?.length
            ? Math.round(d.phases.reduce((s, p) => s + (p.progress ?? 0), 0) / d.phases.length)
            : 0
          const lastYear = d.metrics?.filter(m => !m.type || m.type === 'real').sort((a, b) => b.year - a.year)[0]
          const lockedPhases = d.phases?.filter(p => p.is_locked).length ?? 0

          return (
            <div key={bu.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              {/* BU Header */}
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-brand-50 border border-brand-100 flex items-center justify-center text-xl flex-shrink-0">🏢</div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-base font-bold text-gray-900">{bu.name}</h2>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${hc.bg} ${hc.text} ${hc.border}`}>
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${hc.dot} mr-1`} />
                        {hc.label}
                      </span>
                      {lockedPhases > 0 && (
                        <span className="text-xs text-amber-600 flex items-center gap-1 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                          <Lock size={10} /> {lockedPhases} bloqueada{lockedPhases > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    {bu.description && <p className="text-xs text-gray-400 mt-0.5 truncate">{bu.description}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">{avgProgress}%</div>
                    <div className="text-xs text-gray-400">progreso</div>
                  </div>
                  <button
                    onClick={() => navigate(`/consultancy/${id}/bu/${bu.id}`)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-50 hover:bg-brand-100 text-brand-700 text-xs font-semibold transition-colors border border-brand-100"
                  >
                    Ver BU <ChevronRight size={12} />
                  </button>
                </div>
              </div>

              {/* BU Content */}
              <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-100">

                {/* Col 1: Objective + Pillars */}
                <div className="p-4 space-y-3">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Objetivo estratégico</p>
                  {d.stratObj?.declaracion ? (
                    <p className="text-sm text-gray-700 leading-relaxed line-clamp-4">{d.stratObj.declaracion}</p>
                  ) : (
                    <p className="text-xs text-gray-400 italic">Sin objetivo definido</p>
                  )}
                  {d.pillars?.length > 0 && (
                    <div className="space-y-1.5 pt-1">
                      <p className="text-xs font-semibold text-gray-500">{d.pillars.length} Pilar{d.pillars.length !== 1 ? 'es' : ''}</p>
                      {d.pillars.map((p, i) => (
                        <div key={p.id} className="flex items-start gap-2">
                          <span className="w-5 h-5 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i+1}</span>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-gray-700 truncate">{p.name}</p>
                            {p.kpi && <p className="text-xs text-gray-400 truncate">KPI: {p.kpi}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Col 2: Metrics + Portfolio */}
                <div className="p-4 space-y-3">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Métricas clave</p>
                  {lastYear ? (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                        <p className="text-xs text-gray-400 mb-0.5">{lastYear.year}</p>
                        <p className="text-sm font-bold text-gray-900">{lastYear.ventas ? `${lastYear.ventas} ${lastYear.ventas_unit ?? ''}` : '—'}</p>
                        <p className="text-xs text-gray-400">Ventas</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                        <p className="text-xs text-gray-400 mb-0.5">Part. mdo.</p>
                        <p className="text-sm font-bold text-gray-900">{lastYear.participacion_pct ? `${lastYear.participacion_pct}%` : '—'}</p>
                        <p className="text-xs text-gray-400">Participación</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 italic">Sin métricas</p>
                  )}
                  {d.portfolio?.products?.length > 0 && (
                    <div className="pt-1">
                      <p className="text-xs font-semibold text-gray-500 mb-1.5">Portafolio estratégico</p>
                      {['Mantener','Potenciar','Transformar','Descontinuar'].map(dec => {
                        const count = d.portfolio.products.filter(p => p.decision === dec).length
                        if (!count) return null
                        const colors = { Mantener:'text-blue-600 bg-blue-50', Potenciar:'text-emerald-600 bg-emerald-50', Transformar:'text-amber-600 bg-amber-50', Descontinuar:'text-red-600 bg-red-50' }
                        return (
                          <span key={dec} className={`inline-flex items-center gap-1 mr-1 mb-1 px-2 py-0.5 rounded-full text-xs font-medium ${colors[dec]}`}>
                            {count} {dec}
                          </span>
                        )
                      })}
                    </div>
                  )}
                </div>

                {/* Col 3: Phases progress */}
                <div className="p-4 space-y-2">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Etapas</p>
                  {d.phases?.length > 0 ? d.phases.map((phase, idx) => (
                    <button
                      key={phase.id}
                      onClick={() => navigate(`/consultancy/${id}/bu/${bu.id}/phase/${phase.id}`)}
                      className="w-full text-left group"
                    >
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-xs text-gray-600 truncate group-hover:text-brand-700 transition-colors flex items-center gap-1">
                          {phase.is_locked && <Lock size={9} className="text-amber-500 flex-shrink-0" />}
                          {phase.name}
                        </span>
                        <span className="text-xs font-semibold text-gray-500 flex-shrink-0 ml-2">{phase.progress ?? 0}%</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            (phase.progress ?? 0) >= 100 ? 'bg-emerald-500'
                            : (phase.progress ?? 0) >= 50 ? 'bg-brand-500'
                            : 'bg-gray-300'
                          }`}
                          style={{ width: `${phase.progress ?? 0}%` }}
                        />
                      </div>
                    </button>
                  )) : (
                    <p className="text-xs text-gray-400 italic">Sin etapas</p>
                  )}
                </div>
              </div>
            </div>
          )
        })}

        {bus.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <BarChart3 size={40} className="mx-auto mb-3 opacity-20" />
            <p className="font-medium">Sin unidades de negocio</p>
          </div>
        )}
      </div>
    </div>
  )
}
